import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgModule,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {environment} from '../../../environments/environment';
import * as L from 'leaflet';
import {MapService} from './map.service';
import {Observable} from 'rxjs';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {NgSelectModule} from '@ng-select/ng-select';
import 'leaflet.gridlayer.googlemutant';
import {LeafletModule} from '@bluehalo/ngx-leaflet';
import {LeafletMarkerClusterModule} from '@bluehalo/ngx-leaflet-markercluster';

declare module 'leaflet' {
  namespace gridLayer {
    function googleMutant(options?: any): L.GridLayer;
  }
}

const mapTypeList = [
  {
    name: 'Yandex', url: 'https://vec{s}.maps.yandex.net/tiles?l=map&v=4.55.2&z={z}&x={x}&y={y}&scale=2&lang=ru_RU',
    options: {
      subdomains: ['01', '02', '03', '04'],
      attribution: '<a http="yandex.ru" target="_blank">Яндекс</a>',
      reuseTiles: true,
      updateWhenIdle: false,
    }
  },
  /* SEM-5124  {
      name: 'Open Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      options: {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Points &copy 2012 LINZ',
      }
    },*/
  {
    name: 'Google',
    options: {
      type: 'roadmap',
    }, url: ''
  },
  {
    name: 'Offline',
    options: {
      minZoom: 1,
      maxZoom: 16
    },
    url: environment.backEndUrlPrefix.substring(0, environment.backEndUrlPrefix.indexOf('/sem-rest')) + '/tiles/{z}/{x}_{y}.png'
  }
];

export enum MapType {
  Yandex = 'Yandex',
  /* SEM-5124 OpenStreet = 'OpenStreet',*/
  Google = 'Google',
  Offline = 'Offline'
}

@Component({
  selector: 'in-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnInit, AfterViewInit, AfterContentChecked, OnChanges {
  loadAPI: Promise<any>;

  @Input() center: L.LatLng = new L.LatLng(55.71657934, 37.7278688);
  @Input() id = null;
  @Input() layers = null;
  @Input() zoom = 11;
  @Input() mapType: MapType = this.getAvailableMapTypes()[0];
  @Output() init: EventEmitter<any> = new EventEmitter();
  @Output() mapZoomEnd: EventEmitter<any> = new EventEmitter();
  @Output() mapMoveEnd: EventEmitter<any> = new EventEmitter();
  mapLayers = {
    [MapType.Yandex]: {layer: L.tileLayer(mapTypeList[0].url, mapTypeList[0].options), crs: L.CRS.EPSG3395},
    /* SEM-5124 [MapType.OpenStreet]: {layer: L.tileLayer(mapTypeList[1].url, mapTypeList[1].options), crs: L.CRS.EPSG3857},*/
    [MapType.Google]: {
      layer: L.gridLayer.googleMutant({
        maxZoom: 24,
        type: 'roadmap'
      }),
      crs: L.CRS.EPSG3857
    },
    [MapType.Offline]: {
      layer: L.tileLayer(mapTypeList[mapTypeList.length - 1].url, mapTypeList[mapTypeList.length - 1].options),
      crs: L.CRS.EPSG3857
    }
  }; /* SEM-5124 */
  options: any;
  private message$: Observable<any>;
  private subscriber: any;
  protected fitBounds;
  private map: L.Map;

  constructor(private communication: MapService, private cdr: ChangeDetectorRef) {
    if (this.id) {
      this.message$ = communication.message$[this.id];
      this.subscriber = this.message$.subscribe(this.onMessage.bind(this));
    }
  }


  ngOnInit() {
    if (this.id !== null) {
      this.options = this.buildMapOptions();
    }
  }

  ngAfterViewInit() {
    return;
  }


  ngAfterContentChecked() {
    return;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.mapType && changes.mapType.currentValue !== changes.mapType.previousValue && this.map) {

      this.map.removeLayer(this.mapLayers[changes.mapType.previousValue].layer);
      if (this.mapType === MapType.Google && !this.map.hasLayer(this.mapLayers[MapType.Google].layer)) {
        this.mapLayers[MapType.Google].layer.addTo(this.map);
      }

      if (this.mapType === MapType.Offline && !this.map.hasLayer(this.mapLayers[MapType.Offline].layer)) {
        this.mapLayers[MapType.Offline].layer.addTo(this.map);
      }

      if (this.mapLayers[changes.mapType.previousValue].crs !== this.mapLayers[changes.mapType.currentValue].crs) {

        const center = this.map.getCenter();
        const zoom = this.map.getZoom();
        this.map.options.crs = this.mapLayers[changes.mapType.currentValue].crs;
        (this.map as any)._resetView(center, zoom, false);
      }
    }
  }


  public getAvailableMapTypes(): MapType[] {
    const haveInternetAccess = true;
    const offlineMapsRequired = true; // TODO: придумать решение с автоматической сборкой
    const ret = [];
    if (haveInternetAccess) {
      const google = window['google'];
      if (!google || !google.maps) {
        const url2googlemaps = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCM36DzniNk4BRppQwLNkxny3sQVxk_8qY&callback=Function.prototype`;
        this.loadAPI = new Promise((resolve) => {
          this.loadScript(url2googlemaps);
        });
      }
      ret.push(MapType.Yandex);
      /* SEM-5124 ret.push(MapType.OpenStreet);*/
      ret.push(MapType.Google);
    }
    if (offlineMapsRequired) {
      ret.push(MapType.Offline);
    }
    return ret;
  }

  public loadScript(url) {
    const node = document.createElement('script');
    node.src = url;
    node.type = 'text/javascript';
    node.async = true;
    node.charset = 'utf-8';
    document.getElementsByTagName('head')[0].appendChild(node);
  }

  onMapReady(map) {
    this.map = map;
    this.init.emit(map);
  }

  onMapZoomEnd(e) {
    if (this.map) {
      this.mapZoomEnd.emit(this.map.getZoom());
    }
  }

  onMapMoveEnd(e) {
    if (this.map) {
      this.mapMoveEnd.emit(this.map.getCenter());
    }
  }

  /*
  * Определяет параметры для выбранного типа карты
  * */
  public buildMapOptions(): L.MapOptions {
    let options;
    if (this.mapType === MapType.Yandex) {
      options = {
        attributionControl: false,
        center: this.center,
        zoom: this.zoom,
        zoomControl: false,
        layers: [this.mapLayers[MapType.Yandex].layer], crs: this.mapLayers[MapType.Yandex].crs
      };
      /* SEM-5124   } else if (this.mapType === MapType.OpenStreet) {
            options = {
              center: this.center,
              zoom: this.zoom,
              zoomControl: false,
              layers: [this.mapLayers[MapType.OpenStreet].layer], crs: this.mapLayers[MapType.OpenStreet].crs
            };*/
    } else if (this.mapType === MapType.Google) {
      options = {
        attributionControl: false,
        center: this.center,
        zoom: this.zoom,
        maxZoom: 24,
        zoomControl: false,
        layers: [this.mapLayers[MapType.Google].layer],
        crs: this.mapLayers[MapType.Google].crs
      };
    } else if (this.mapType === MapType.Offline) {
      options = {
        attributionControl: false,
        center: this.center,
        zoom: this.zoom,
        zoomControl: false,
        layers: [
          this.mapLayers[MapType.Offline].layer
        ],
        crs: this.mapLayers[MapType.Offline].crs
      };
    }
    return options;
  }

  private onMessage(data) {
  }

}


@NgModule({
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgSelectModule,
    LeafletModule, //.forRoot(),
    LeafletMarkerClusterModule,
  ],
  declarations: [
    MapComponent,
  ],
  providers: [MapService],
  exports: [
    MapComponent,
  ],
})
export class InsparkMapModule {
}
